import { Link } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import type { BlogContent } from '../types/cmsPages';

export default function BlogListPage() {
  const { pages: publishedPages, loading } = useContent();
  const pages = publishedPages.filter(page => page.type === 'blog');

  if (loading) {
    return (
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-brand-navy overflow-hidden">
          <div className="absolute inset-0">
            <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="blog-list-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1" fill="white" />
                  <path d="M0 0L40 40M40 0L0 40" stroke="white" strokeWidth="0.5" opacity="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#blog-list-grid)" />
            </svg>
          </div>
          <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: '89px', paddingBottom: '89px' }}>
            <div className="max-w-3xl">
              <h1 className="text-[32px] leading-[1.15] sm:text-[36px] md:text-4xl lg:text-[40px] font-serif font-bold text-white">
                Our <span className="text-brand-orange">Blog</span>
              </h1>
            </div>
          </div>
        </section>
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-brand-orange border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" tabIndex={-1}>
      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="blog-list-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1" fill="white" />
                <path d="M0 0L40 40M40 0L0 40" stroke="white" strokeWidth="0.5" opacity="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#blog-list-grid)" />
          </svg>
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-brand-orange/8 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: '89px', paddingBottom: '89px' }}>
          <nav className="flex items-center gap-2 mb-5 sm:mb-6">
            <Link to="/" className="text-white/50 text-sm hover:text-white/80 transition-colors">Home</Link>
            <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-brand-orange text-sm font-medium">Blog</span>
          </nav>

          <div className="max-w-3xl">
            <h1 className="text-[32px] leading-[1.15] sm:text-[36px] md:text-4xl lg:text-[40px] xl:text-[44px] font-serif font-bold text-white mb-3 sm:mb-4">
              Our <span className="text-brand-orange">Blog</span>
            </h1>
            <p className="text-base sm:text-[15px] lg:text-base text-white/70 leading-relaxed max-w-2xl">
              Tips, insights, and updates from the Best Neighbor Home Services team.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        {pages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pages.map((page) => {
              const content = page.content as BlogContent;
              return (
                <Link
                  key={page.id}
                  to={`/blog/${page.slug}`}
                  className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {content.featured_image ? (
                    <div className="h-48 overflow-hidden">
                      <img decoding="async" loading="lazy"
                        src={content.featured_image}
                        alt={page.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-brand-off-white flex items-center justify-center">
                      <svg className="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      {content.author && <span>{content.author}</span>}
                      {content.author && content.publish_date && <span className="w-1 h-1 bg-gray-300 rounded-full" />}
                      {content.publish_date && (
                        <span>{new Date(content.publish_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      )}
                    </div>
                    <h3 className="text-lg font-serif font-bold text-brand-navy mb-2 group-hover:text-brand-orange transition-colors">
                      {page.title}
                    </h3>
                    {content.excerpt && (
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{content.excerpt}</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
